import CheckboxGroup from '@ingka/checkbox-group';
import FormField from '@ingka/form-field';
import type { BaseLotteryInput } from '@lotta/self-service/lottery-fe/types-form';
import type { Control, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Values = Pick<BaseLotteryInput, 'retailChannels'>;

export type ControlledRetailChannelsCheckboxesProps<TFormData extends Values = Values> = {
  control: Control<TFormData>;
  errorMessage: string | undefined;
};

export const ControlledRetailChannelsCheckboxes = <TFormData extends Values = Values>({
  errorMessage,
  control,
}: ControlledRetailChannelsCheckboxesProps<TFormData>) => {
  const { t } = useTranslation();

  const retailChannelCheckBoxes = [
    {
      id: 'ALL',
      name: 'ALL',
      label: t('general-all'),
    },
    {
      id: 'ONLINE',
      name: 'ONLINE',
      label: t('general-online'),
    },
    {
      id: 'STORE',
      name: 'STORE',
      label: t('general-store'),
    },
  ] as const;

  if (errorMessage) {
    errorMessage = `${t(errorMessage)}`;
  }

  return (
    <FormField
      shouldValidate={!!errorMessage}
      valid={!errorMessage}
      validation={{
        msg: errorMessage,
        type: 'error',
      }}
    >
      <Controller
        render={({ field: { onChange, onBlur, value } }) => (
          <CheckboxGroup
            name={`${t('lottery-form-retail-channels')}`}
            list={retailChannelCheckBoxes.map((item) => ({
              ...item,
              checked: value?.includes(item.id) || value?.includes('ALL'),
              // If 'ALL' is selected, disable all other checkboxes
              disabled: value?.includes('ALL') && item.id !== 'ALL',
              onBlur,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                if (Array.isArray(value)) {
                  const valuesWithoutThisId = value.filter((id) => id !== item.id);
                  if (e.currentTarget.checked) {
                    onChange([...valuesWithoutThisId, item.id]);
                  } else {
                    onChange(valuesWithoutThisId);
                  }
                }
              },
            }))}
          />
        )}
        name={'retailChannels' as Path<TFormData>}
        control={control}
      />
    </FormField>
  );
};
